// @ts-nocheck
/**
 * swym-wishlist-button.js — wires the theme's existing presentation-only
 * wishlist buttons (snippets/wishlist-button.liquid, [data-wishlist-btn]) to
 * Swym's public JS SDK, in SDK-only mode. No buttons are created here and
 * Swym's own default UI/click handlers are never used — only the documented
 * `swat` API is called directly:
 *   https://developers.getswym.com/reference/build-a-custom-wishlist-button-1
 *   https://developers.getswym.com/docs/wishlist-events
 *
 * SDK field names (per docs): empi = Shopify product id, epi = Shopify
 * variant id, du = canonical product URL, lid = wishlist list id.
 */

(function () {
  'use strict';

  var SELECTOR = '[data-wishlist-btn]';

  // ─── Reading product data already present on the button ───────────────────
  // (data-product-id / data-variant-id / data-product-url are rendered by
  // snippets/wishlist-button.liquid — nothing is hardcoded here.)

  function productFromBtn(btn) {
    var rawUrl = btn.dataset.productUrl || '';
    var cleanPath = rawUrl.split('?')[0].split('#')[0];
    var du;
    try {
      du = new URL(cleanPath, window.location.origin).href;
    } catch (e) {
      du = window.location.origin + cleanPath;
    }
    return {
      empi: btn.dataset.productId,
      epi: btn.dataset.variantId,
      du: du,
      source: btn.classList.contains('wishlist-btn--pdp') ? 'pdp' : 'collections-grid',
    };
  }

  // ─── Wishlist-state lookup (against the SDK's own cached lists) ───────────
  // swat.fetchLists() returns every list the visitor has. The `!lty` check
  // below is the documented way to identify the real Wishlist, but it never
  // matches on this account: Smart Save ("Saved for Me") auto-creates a
  // second list that also comes back with lty: "wl", and Swym support has
  // confirmed (2026-08-31 thread) there is currently no API/JS SDK field
  // that distinguishes the two — lname is the only usable signal. So every
  // call here actually resolves through the fallback loop, matching the
  // list by name. ensureListId() below creates new lists with this exact
  // lname, so it stays consistent for both new and existing visitors.
  // Disabling Smart Save in the Swym admin would also fix this (fetchLists
  // would then return only the real Wishlist), but that's a business
  // decision, not something to change here. Keep the `!lty` check first in
  // case Swym adds SDK support for this later.
  var WISHLIST_FALLBACK_LNAME = 'My Wishlist';

  function findWishlist(lists) {
    var all = lists || [];
    for (var i = 0; i < all.length; i++) {
      if (!all[i].lty) return all[i];
    }
    for (var j = 0; j < all.length; j++) {
      if (all[j].lname === WISHLIST_FALLBACK_LNAME) return all[j];
    }
    return null;
  }

  function isWishlisted(empi, epi) {
    var wishlist = findWishlist(window.swymWishLists);
    if (!wishlist) return false;
    var contents = wishlist.listcontents || [];
    for (var j = 0; j < contents.length; j++) {
      if (String(contents[j].empi) === String(empi) && String(contents[j].epi) === String(epi)) {
        return true;
      }
    }
    return false;
  }

  function paintButton(btn) {
    var empi = btn.dataset.productId;
    var epi = btn.dataset.variantId;
    if (!empi) return;
    var active = isWishlisted(empi, epi);
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', String(active));
    var title = btn.dataset.productTitle;
    var label = active ? 'Remove from wishlist' : 'Add to wishlist';
    btn.setAttribute('aria-label', title ? label + ': ' + title : label);
  }

  function paintAllButtons() {
    document.querySelectorAll(SELECTOR).forEach(paintButton);
  }

  // ─── SDK plumbing ───────────────────────────────────────────────────────
  // swat.fetchLists() is the documented way to read the visitor's current
  // wishlist contents (used both for the initial render and to determine
  // wishlisted state on click). Cached on window.swymWishLists so every
  // button on the page shares one fetch.

  function fetchLists(swat) {
    return new Promise(function (resolve) {
      if (window.swymWishLists) {
        resolve(window.swymWishLists);
        return;
      }
      swat.fetchLists({
        callbackFn: function (lists) {
          window.swymWishLists = lists || [];
          var wishlist = findWishlist(window.swymWishLists);
          window.swymSelectedListId = (wishlist && wishlist.lid) || window.swymSelectedListId || null;
          resolve(window.swymWishLists);
        },
        errorFn: function () {
          window.swymWishLists = window.swymWishLists || [];
          resolve(window.swymWishLists);
        },
      });
    });
  }

  // Forces a re-fetch (used whenever the SDK reports a change that may not
  // have originated from our own click — e.g. Swym's own quick-save widget
  // elsewhere on the page, or another tab). Not used right after our own
  // add/remove: swat.fetchLists() can briefly return pre-update data in the
  // same tick as a successful addToList/deleteFromList (server/SDK-side
  // consistency lag), which would repaint the button back to its old state
  // even though the write already succeeded. Our own add/remove instead
  // updates the local cache directly — see addLocalItem/removeLocalItem.
  function refreshFromServer(swat) {
    window.swymWishLists = null;
    return fetchLists(swat).then(paintAllButtons);
  }

  // Swym's own addedToWishlist/removedFromWishlist SDK events (listened to
  // below, to catch changes from other tabs or Swym's own widgets) also
  // fire for adds/removes WE just made through this file. Without this
  // guard, that event's refreshFromServer() would re-run the same stale
  // re-fetch race described above a moment after our own optimistic paint,
  // making the button flash active then immediately revert. Any local
  // mutation marks a short cooldown window during which that event is
  // treated as an echo of our own action rather than an external change.
  var lastLocalMutationAt = 0;
  var LOCAL_MUTATION_COOLDOWN_MS = 3000;
  function markLocalMutation() {
    lastLocalMutationAt = Date.now();
  }

  // Reflects a just-confirmed add/remove into the cached wishlist contents
  // so the button repaints correctly without waiting on a re-fetch.
  function addLocalItem(product) {
    markLocalMutation();
    var wishlist = findWishlist(window.swymWishLists);
    if (!wishlist) {
      // First-ever add for this visitor: ensureListId() just created the
      // list via swat.createList(), so the cache has no entry for it yet.
      if (!window.swymSelectedListId) return;
      wishlist = { lid: window.swymSelectedListId, lty: null, listcontents: [] };
      window.swymWishLists = (window.swymWishLists || []).concat([wishlist]);
    }
    wishlist.listcontents = wishlist.listcontents || [];
    wishlist.listcontents.push({ empi: product.empi, epi: product.epi });
  }

  function removeLocalItem(empi, epi) {
    markLocalMutation();
    var wishlist = findWishlist(window.swymWishLists);
    if (!wishlist) return;
    wishlist.listcontents = (wishlist.listcontents || []).filter(function (item) {
      return !(String(item.empi) === String(empi) && String(item.epi) === String(epi));
    });
  }

  // swat.addToList/deleteFromList both require an existing list id. Create
  // the default list on first use if the visitor doesn't have one yet.
  function ensureListId(swat) {
    return fetchLists(swat).then(function () {
      if (window.swymSelectedListId) return window.swymSelectedListId;
      return new Promise(function (resolve) {
        swat.createList(
          { lname: WISHLIST_FALLBACK_LNAME },
          function (res) {
            window.swymSelectedListId = res && res.lid;
            resolve(window.swymSelectedListId);
          },
          function () {
            resolve(null);
          }
        );
      });
    });
  }

  function notifyError(swat, message) {
    if (swat.ui && typeof swat.ui.showErrorNotification === 'function') {
      swat.ui.showErrorNotification({ message: message });
    }
  }

  // ─── Click handling ─────────────────────────────────────────────────────

  function handleClick(swat, btn) {
    if (btn.disabled) return; // prevents duplicate clicks while a request is in flight

    var product = productFromBtn(btn);
    if (!product.empi || !product.epi) return;

    var wasActive = isWishlisted(product.empi, product.epi);
    btn.disabled = true;
    btn.classList.add('is-loading');

    ensureListId(swat).then(function (lid) {
      var done = function () {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      };

      if (!lid) {
        done();
        notifyError(swat, 'Could not update your wishlist. Please try again.');
        return;
      }

      // Started before the request resolves: Swym's addedToWishlist/
      // removedFromWishlist events (listened to below) can fire before our
      // own success callback runs, so the cooldown has to cover that order
      // too, not just the moment we hear back.
      markLocalMutation();

      if (wasActive) {
        swat.deleteFromList(
          lid,
          product,
          function () {
            removeLocalItem(product.empi, product.epi);
            paintAllButtons();
            done();
          },
          function () {
            done();
            notifyError(swat, 'Could not remove item from wishlist.');
          }
        );
      } else {
        swat.addToList(
          lid,
          product,
          function () {
            addLocalItem(product);
            paintAllButtons();
            done();
            // Lets assets/header.js slide a hidden sticky header back into view.
            document.dispatchEvent(new CustomEvent('wishlist:added', { detail: { product: product } }));
          },
          function () {
            done();
            notifyError(swat, 'Could not add item to wishlist.');
          }
        );
      }
    });
  }

  // ─── PDP variant tracking ───────────────────────────────────────────────
  // Keeps the PDP button's data-variant-id in sync with the selected
  // variant, so add/remove always targets the variant currently shown.

  function onVariantChange(e) {
    var section = e.target && e.target.closest ? e.target.closest('.shopify-section') : null;
    if (!section) return;
    var btn = section.querySelector('.wishlist-btn--pdp[data-wishlist-btn]');
    if (!btn) return;
    var input = section.querySelector('[ref="variantId"]') || section.querySelector('input[name="id"]');
    if (input && input.value) {
      btn.dataset.variantId = input.value;
      paintButton(btn);
    }
  }

  // ─── Bootstrap ───────────────────────────────────────────────────────────
  // All SDK calls happen inside SwymCallbacks, per Swym's docs: accessing
  // window._swat directly can race the SDK's own load/init.

  var repaintTimer = null;
  function scheduleRepaint() {
    clearTimeout(repaintTimer);
    repaintTimer = setTimeout(paintAllButtons, 80);
  }

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(function (swat) {
    if (window.__swymWishlistButtonsInit) return;
    window.__swymWishlistButtonsInit = true;

    fetchLists(swat).then(paintAllButtons);

    document.body.addEventListener('click', function (e) {
      var btn = e.target.closest(SELECTOR);
      if (!btn) return;
      e.preventDefault();
      handleClick(swat, btn);
    });

    document.addEventListener('variant:update', onVariantChange);
    document.addEventListener('variant:selected', onVariantChange);

    // Sync with changes that happen outside our own click handler (another
    // tab, or Swym's own default UI elsewhere on the storefront).
    if (swat.evtLayer && swat.JSEvents) {
      swat.evtLayer.addEventListener(swat.JSEvents.addedToWishlist, function () {
        if (Date.now() - lastLocalMutationAt < LOCAL_MUTATION_COOLDOWN_MS) return;
        refreshFromServer(swat);
      });
      swat.evtLayer.addEventListener(swat.JSEvents.removedFromWishlist, function () {
        if (Date.now() - lastLocalMutationAt < LOCAL_MUTATION_COOLDOWN_MS) return;
        refreshFromServer(swat);
      });
    }

    // Cart sync for Swym's wishlist-page "Add to Cart" lives in
    // assets/swym-cart-sync.js (loaded only on the Swym wishlist template).

    // Newly-rendered buttons: paginated/infinite-scroll grids, quick-add,
    // Shopify theme-editor section reloads.
    document.addEventListener('shopify:section:load', scheduleRepaint);
    var observer = new MutationObserver(function (mutations) {
      if (mutations.some(function (m) { return m.addedNodes.length > 0; })) scheduleRepaint();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Small public surface so other theme scripts (e.g. the product carousel's
    // color-pill switcher) can repaint a button after retargeting its dataset.
    window.SwymWishlistButtons = {
      refresh: paintButton,
      refreshAll: paintAllButtons,
    };
  });
})();
